/**
 * The tab strip the rep exchange view is built from (DG.41).
 *
 * ## Why this is a new primitive
 *
 * None of the ten in `primitives.tsx` is one, and none of them holds state or handles a key. `Modal.tsx`
 * is already the precedent for a component that does both living in its own file, and this is the second.
 *
 * ## It owns the panel as well as the strip
 *
 * `aria-controls` on a tab and `aria-labelledby` on its panel are a pair of ids that have to agree, and
 * a strip-only component pushes that agreement onto every caller - where it is invisible when it breaks,
 * because nothing renders differently. One `useId()` here derives both, so they cannot drift.
 *
 * Only the selected panel is mounted, rather than four with `hidden` on three of them. That is the height
 * saving the whole change is for: the widget's frame is sized to its content by `iqz-ddds:resize`, so a
 * panel that exists costs the host real pixels. It is safe here only because every rep panel takes data
 * and callbacks and holds no state of its own, and because the send queue deliberately renders *outside*
 * the tabs - a progress bar unmounted by a tab click would be the DG.22 defect in a new place.
 *
 * ## Both arrow axes, always, and that is a knowing deviation
 *
 * The ARIA authoring practice binds Left/Right to a horizontal tablist and Up/Down to a vertical one. We
 * accept all four regardless, because we cannot know which we are: `orientation` is a *preference* a host
 * sends, `surface.ts` falls back to a horizontal strip below 60rem, and there is no viewport check in
 * JavaScript anywhere in this codebase by policy. Binding the keys to the requested orientation would
 * therefore hand a rep the wrong keys in exactly the case the fallback exists for. `aria-orientation` is
 * omitted for the same reason - the stylesheet can contradict it, and a stale attribute is worse than an
 * absent one.
 */
import { useCallback, useId, useRef } from 'react';
import type { ReactNode } from 'react';

export interface TabItem<Id extends string> {
  readonly id: Id;
  readonly label: string;
  /**
   * Drawn beside the label. Absent draws none.
   *
   * A caller should omit it rather than pass zero: a `0` badge still reads as a badge, which is the
   * opposite of what it says.
   */
  readonly count?: number;
  /**
   * Something changed here while the rep was looking at another tab.
   *
   * Narrower than "changed", and deliberately so - see `ExchangeView`, which only ever raises this from
   * a poll and never from a write of the rep's own.
   */
  readonly changed?: boolean;
}

export interface TabsProps<Id extends string> {
  readonly items: readonly TabItem<Id>[];
  readonly selected: Id;
  readonly onSelect: (id: Id) => void;
  /** The tablist's accessible name. A tablist without one is announced as nothing at all. */
  readonly label: string;
  /**
   * Which way the strip runs, as a preference rather than a guarantee.
   *
   * A column needs width the panel does not control - inside the widget it gets whatever pane the CRM
   * allocated - so `surface.ts` renders a horizontal strip below 60rem whatever this says. The contract
   * with the host says as much rather than letting them discover it.
   */
  readonly orientation?: 'horizontal' | 'vertical';
  /** The selected panel's body. Only the selected one is rendered; see the file comment. */
  readonly children: ReactNode;
}

/** Where an arrow key lands, wrapping at both ends so the strip has no dead end. */
function step(count: number, from: number, by: number): number {
  return (from + by + count) % count;
}

/** A movement within the strip: one step either way, or straight to an end. */
type Move = 1 | -1 | 'first' | 'last';

/**
 * What a key means here, or nothing if it means nothing here.
 *
 * Module level rather than inside the component, so it is not rebuilt on every render, and a lookup of
 * early returns rather than a chain of conditionals because the four cases are peers - reading it should
 * not mean holding three earlier tests in mind to understand the fourth.
 *
 * Both axes map to the same movement deliberately. See the file comment: orientation is a preference the
 * stylesheet may override, so this cannot know which way the strip is actually running.
 */
function moveFor(key: string): Move | undefined {
  if (key === 'ArrowLeft' || key === 'ArrowUp') return -1;
  if (key === 'ArrowRight' || key === 'ArrowDown') return 1;
  if (key === 'Home') return 'first';
  if (key === 'End') return 'last';
  return undefined;
}

/** The index a movement lands on, given where it started and how many tabs there are. */
function indexFor(move: Move, from: number, count: number): number {
  if (move === 'first') return 0;
  if (move === 'last') return count - 1;
  return step(count, from, move);
}

export function Tabs<Id extends string>({
  items,
  selected,
  onSelect,
  label,
  orientation = 'horizontal',
  children,
}: TabsProps<Id>): React.JSX.Element {
  const base = useId();
  const tabId = (id: Id): string => `${base}tab-${id}`;
  const panelId = (id: Id): string => `${base}panel-${id}`;

  /*
   * The buttons, so the selection can take focus with it.
   *
   * Without this, arrowing leaves focus on the element that just became `tabIndex={-1}`, so the next Tab
   * jumps somewhere unrelated and the next arrow does nothing. All four stay mounted, so the node is
   * always there to focus.
   */
  const stops = useRef(new Map<Id, HTMLButtonElement | null>());

  const move = useCallback(
    (from: Id, by: Move): void => {
      const at = items.findIndex((item) => item.id === from);
      if (at === -1) return;

      const next = items[indexFor(by, at, items.length)];
      if (next === undefined || next.id === from) return;

      onSelect(next.id);
      stops.current.get(next.id)?.focus();
    },
    [items, onSelect],
  );

  /*
   * On each button rather than on the strip or on the document.
   *
   * `Modal.tsx` listens on the document because a `<dialog>` is not an interactive element and a handler
   * on one is a defect a linter is right to report. A `<button>` is interactive, so the handler belongs
   * where the key was pressed.
   *
   * Automatic activation - the arrow selects rather than only moving - because selecting a tab here reads
   * data already in hand and destroys nothing, and because it is what makes arrowing past a tab clear the
   * marker on it.
   */
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, id: Id): void => {
      const by = moveFor(event.key);

      // Enter and Space are the button's own business, and the arrow has already selected.
      if (by === undefined) return;

      event.preventDefault();
      move(id, by);
    },
    [move],
  );

  return (
    <div className={`ddds-tabs${orientation === 'vertical' ? ' ddds-tabs--vertical' : ''}`}>
      <div className="ddds-tabs__strip" role="tablist" aria-label={label}>
        {items.map((item) => (
          <button
            key={item.id}
            id={tabId(item.id)}
            ref={(node) => {
              stops.current.set(item.id, node);
            }}
            className="ddds-tabs__tab"
            type="button"
            role="tab"
            aria-selected={item.id === selected}
            aria-controls={panelId(item.id)}
            // Roving: one Tab stop for the whole strip, and the arrows move within it.
            tabIndex={item.id === selected ? 0 : -1}
            onClick={() => onSelect(item.id)}
            onKeyDown={(event) => onKeyDown(event, item.id)}
          >
            {item.label}
            {item.count !== undefined && <span className="ddds-tabs__count">{item.count}</span>}
            {item.changed === true && (
              <>
                <span className="ddds-tabs__dot" aria-hidden="true" />
                {/* The dot is a colour and a shape, which is not a state. The word is what carries it. */}
                <span className="ddds-visually-hidden">new</span>
              </>
            )}
          </button>
        ))}
      </div>

      <div
        className="ddds-tabs__panel"
        role="tabpanel"
        id={panelId(selected)}
        aria-labelledby={tabId(selected)}
        // Focusable, so Tab out of the strip reaches the panel it just selected rather than skipping it.
        tabIndex={0}
      >
        {children}
      </div>
    </div>
  );
}
